/* Margin Finder · Assinaturas: o que o `agent_assinaturas` achou, um usuário por vez.
 *
 * A economia aqui deixou de ser uma FAIXA (conservadora vs agressiva, do schema
 * anterior) e virou um número só: `custo_mensal_atual_brl` menos
 * `custo_mensal_otimizado_brl`, anualizado pelo agente. Um intervalo onde há um
 * ponto seria inventar incerteza que o dado não tem mais.
 *
 * O gasto por categoria é somado AQUI, no cliente: o schema 4.0 deixou de mandar
 * `panorama.por_categoria` e a soma é um reduce de quatro linhas — cruzar a rede de
 * novo por ela seria pior.
 *
 * Duas armadilhas do formato novo, ambas com custo real de tela:
 *   · `como_obter` é uma STRING aqui e uma LISTA em seguros. Renderizar como lista
 *     imprimiria uma letra por marcador.
 *   · `auditoria.regras_violadas` — sem o `_de_piso_` que os outros três agentes têm.
 */

import { ChartFrame, Card } from "../../components/ui";
import type { Assinatura, AssinaturasUser, Oportunidade } from "../../lib/api";
import { brl2, fmtMes } from "../../lib/format";
import { RelatorioEmTexto } from "./Relatorio";
import {
  Barras, BlocoAuditoria, CONFIANCA, Facts, Hero, Provenancia, SecaoMargin,
  economiaLabel, host, lista, money, ou, rotulo,
} from "./comum";

/* ── rótulos ─────────────────────────────────────────────────────────────── */

const CATEGORIA: Record<string, string> = {
  ia_e_software: "IA e software",
  beneficios_marketplace: "benefícios e marketplace",
  streaming_video: "streaming de vídeo",
  agregador: "agregador",
  musica_audio: "música e áudio",
  outros: "outros",
  delivery_conveniencia: "delivery e conveniência",
  armazenamento_cloud: "armazenamento em nuvem",
  leitura_educacao: "leitura e educação",
  bem_estar_fitness: "bem-estar e fitness",
};

const TIPO: Record<string, string> = {
  auditar_no_painel: "auditar no painel",
  trocar_de_plano: "trocar de plano",
  preco_acima_do_catalogo: "preço acima do catálogo",
  trocar_por_anual: "trocar por anual",
  combo_substitui_avulsos: "combo substitui avulsos",
};

const ORIGEM: Record<string, string> = {
  consolidado: "consolidado da janela",
  corrigido_anuidade: "anuidade rateada",
};

const STATUS: Record<string, string> = {
  analise: "análise",
  sem_assinaturas: "sem assinatura no extrato",
};

/* ── os cartões ──────────────────────────────────────────────────────────── */

function OportunidadeCard({ o }: { o: Oportunidade }) {
  return (
    <article className="mf-achado">
      <div className="mf-plan-papel">{rotulo(TIPO, o.tipo)}</div>
      <h3>{o.acao}</h3>
      {o.economia_anual_brl !== null && (
        <div className="mf-achado-impacto">{money(o.economia_anual_brl)} por ano</div>
      )}
      <div className="chips">
        {lista(o.servicos).map((s) => <span className="chip" key={s}>{s}</span>)}
        <span className={o.confianca === "baixa" ? "tag alta" : "tag media"}>
          {rotulo(CONFIANCA, o.confianca)}
        </span>
      </div>
      {o.plano_catalogo && (
        <p className="mf-nota">
          {o.plano_catalogo}
          {o.preco_catalogo_mensal_brl !== null
            && <> — {money(o.preco_catalogo_mensal_brl)}/mês no catálogo</>}
        </p>
      )}
      {o.o_que_perde && <p className="mf-atencao">o que perde: {o.o_que_perde}</p>}
      {o.fonte_url && (
        <a className="mf-source" href={o.fonte_url} target="_blank" rel="noreferrer">
          fonte: {host(o.fonte_url)}
        </a>
      )}
    </article>
  );
}

function AssinaturaCard({ s }: { s: Assinatura }) {
  return (
    <article className="mf-assin">
      <div className="mf-assin-head">
        <div className="mf-assin-id">
          <h3>{ou(s.rotulo, s.servico)}</h3>
          <div className="mf-assin-meta">
            {rotulo(CATEGORIA, s.categoria)}
            {s.plano_identificado && <> · {s.plano_identificado}</>}
          </div>
        </div>
        <div className="mf-assin-num">
          <div className="v">{money(s.custo_mensal_brl)}<small>/mês</small></div>
          <div className="d">
            {s.custo_mensal_brl === null ? "—" : `${money(s.custo_mensal_brl * 12)} por ano`}
          </div>
        </div>
      </div>
      <div className="chips">
        <span className="chip">{rotulo(CATEGORIA, s.categoria)}</span>
        {!s.no_catalogo && <span className="tag alta">fora do catálogo</span>}
        {s.origem_custo === "corrigido_anuidade" && (
          <span className="tag media">{rotulo(ORIGEM, s.origem_custo)}</span>
        )}
      </div>
    </article>
  );
}

/* ── a sessão ────────────────────────────────────────────────────────────── */

export function Assinaturas() {
  return (
    <SecaoMargin<AssinaturasUser>
      chave="assinaturas"
      vazio="Usuário sem análise de assinaturas."
      optionLabel={economiaLabel}
    >
      {(user) => <AssinaturasDetalhe user={user} />}
    </SecaoMargin>
  );
}

function AssinaturasDetalhe({ user }: { user: AssinaturasUser }) {
  const a = user.analise;
  const auditoria = a.auditoria ?? {} as typeof a.auditoria;
  const janela = auditoria.janela ?? {} as typeof auditoria.janela;
  const assinaturas = [...lista(a.assinaturas)]
    .sort((x, y) => (y.custo_mensal_brl ?? 0) - (x.custo_mensal_brl ?? 0));
  const oportunidades = [...lista(a.oportunidades)].sort(
    (x, y) => (x.economia_anual_brl === null ? 1 : 0) - (y.economia_anual_brl === null ? 1 : 0)
      || (y.economia_anual_brl ?? 0) - (x.economia_anual_brl ?? 0));

  // O schema 4.0 deixou de mandar `panorama.por_categoria`; a soma sai daqui.
  const porCategoria = new Map<string, { valor: number; qtd: number }>();
  for (const s of assinaturas) {
    const cur = porCategoria.get(s.categoria) ?? { valor: 0, qtd: 0 };
    porCategoria.set(s.categoria, {
      valor: cur.valor + (s.custo_mensal_brl ?? 0),
      qtd: cur.qtd + 1,
    });
  }
  const barras = [...porCategoria.entries()]
    .sort((x, y) => y[1].valor - x[1].valor)
    .map(([cat, v]) => ({
      key: cat,
      rotulo: rotulo(CATEGORIA, cat),
      valor: v.valor,
      qtd: `${v.qtd}×`,
    }));

  const semAssinaturas = a.status === "sem_assinaturas";

  return (
    <div className="mf-page">
      {/* 1 · a decisão */}
      <Card>
        <Hero
          estado={user.estado_economia}
          titulo="economia estimada"
          valor={user.economia_anual_brl === null
            ? "sem base comparável"
            : `R$ ${brl2(user.economia_anual_brl)}`}
          detalhe={user.economia_anual_brl === null
            ? "O agente não achou assinatura para comparar."
            : <>por ano · {money(user.economia_mensal_brl)}/mês</>}
          lado={a.custo_mensal_atual_brl === null ? undefined : {
            k: "sai hoje",
            v: `${money(a.custo_mensal_atual_brl)}/mês`,
            d: <>
              {money(a.custo_anual_atual_brl)} por ano · {assinaturas.length} assinatura(s)
              {a.mes_referencia && <> · ref. {fmtMes(a.mes_referencia, true)}</>}
            </>,
          }}
        />
        <p className="mf-frase">
          {a.status !== "analise" && (
            <span className="tag" style={{ marginRight: 8 }}>{rotulo(STATUS, a.status)}</span>
          )}
          {a.resumo}
        </p>
      </Card>

      {!semAssinaturas && (
        <>
          {/* 2 · a evidência: o panorama */}
          <Card title="Panorama" sub="o run-rate do mês de referência">
            <Facts items={[
              { k: "sai por mês", v: money(a.custo_mensal_atual_brl) },
              { k: "sai por ano", v: money(a.custo_anual_atual_brl) },
              { k: "otimizado por mês", v: money(a.custo_mensal_otimizado_brl) },
              { k: "mês de referência", v: a.mes_referencia ? fmtMes(a.mes_referencia, true) : "—" },
              { k: "meses na janela", v: janela.meses_na_janela?.toString() ?? "—" },
              { k: "assinaturas", v: assinaturas.length.toString() },
            ]} />
            {barras.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="mf-subcard-k">onde o dinheiro está</div>
                <Barras rows={barras} />
              </div>
            )}
          </Card>

          {/* 3 · a ação: o que dá para fazer */}
          {oportunidades.length > 0 && (
            <ChartFrame
              title="O que o agente achou"
              sub="maior economia primeiro; sem valor apurável por último"
              tableHeader={["tipo", "ação", "serviços", "R$/ano", "confiança"]}
              tableRows={oportunidades.map((o) => [
                rotulo(TIPO, o.tipo), o.acao, lista(o.servicos).join(", "),
                o.economia_anual_brl ?? "—", rotulo(CONFIANCA, o.confianca),
              ])}
            >
              <div className="mf-achados">
                {oportunidades.map((o, i) => <OportunidadeCard key={i} o={o} />)}
              </div>
            </ChartFrame>
          )}

          {/* 4 · a evidência: a lista */}
          <ChartFrame
            title="As assinaturas"
            sub="do maior custo mensal para o menor"
            tableHeader={["rótulo", "categoria", "R$/mês", "no catálogo", "origem"]}
            tableRows={assinaturas.map((s) => [
              s.rotulo, rotulo(CATEGORIA, s.categoria), s.custo_mensal_brl ?? "—",
              s.no_catalogo ? "sim" : "não", rotulo(ORIGEM, s.origem_custo),
            ])}
          >
            <div className="mf-assins">
              {assinaturas.map((s, i) => <AssinaturaCard key={i} s={s} />)}
            </div>
          </ChartFrame>

          {/* 5 · a ação: o caminho. STRING, não lista — ver o cabeçalho do arquivo. */}
          {a.como_obter && (
            <Card title="Como obter" sub="o caminho até a economia">
              <p className="mf-nota">{a.como_obter}</p>
            </Card>
          )}
        </>
      )}

      {/* 6 · as ressalvas */}
      <Card title="Premissas e limitações" sub="o que o agente assumiu para chegar aqui">
        {lista(a.premissas).length > 0 && (
          <ul className="mf-why">
            {lista(a.premissas).map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        )}
        <BlocoAuditoria
          limitacoes={lista(auditoria.limitacoes)}
          blocosRecebidos={[]}
          blocosAusentes={[]}
          idsCitados={[]}
          regrasVioladas={lista(auditoria.regras_violadas)}
        >
          <Facts items={[
            { k: "janela", v: `${ou(janela.primeiro_mes && fmtMes(janela.primeiro_mes))} → ${ou(janela.ultimo_mes && fmtMes(janela.ultimo_mes))}` },
            { k: "serviços recebidos", v: auditoria.servicos_recebidos?.toString() ?? "—" },
            { k: "linhas no catálogo", v: auditoria.linhas_catalogo?.toString() ?? "—" },
          ]} />
          {lista(auditoria.sem_linha_no_catalogo).length > 0 && (
            <p className="mf-nota">
              Sem linha no catálogo: {lista(auditoria.sem_linha_no_catalogo).join(", ")}
            </p>
          )}
          {lista(auditoria.fora_da_lista).length > 0 && (
            <p className="mf-nota">
              Fora da lista: {lista(auditoria.fora_da_lista).join(", ")}
            </p>
          )}
        </BlocoAuditoria>
      </Card>

      {/* 7 · o texto do agente */}
      <RelatorioEmTexto userId={user.user_id} agentId="agent_assinaturas" />

      {/* 8 · a procedência */}
      <Provenancia user={user} agentId="agent_assinaturas" />
    </div>
  );
}
